import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Radio, Play, Clock, Trophy, Search, Gavel } from "lucide-react";
import { toast } from "sonner";
import { fmtTime } from "@/lib/format";

const STATUS_ORDER = { live: 0, paused: 1, scheduled: 2, finished: 3 };
const STATUS_STYLE = {
  live: "bg-red-500 text-white",
  paused: "bg-amber-500 text-black",
  scheduled: "bg-zinc-800 text-zinc-300",
  finished: "bg-zinc-700 text-zinc-300",
};

export default function RefereePortal() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [matches, setMatches] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matches");
      setMatches(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return matches
      .filter((m) => {
        if (!term) return true;
        const a = m.team_a?.name?.toLowerCase() || "";
        const b = m.team_b?.name?.toLowerCase() || "";
        return a.includes(term) || b.includes(term) || (m.court || "").toLowerCase().includes(term);
      })
      .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));
  }, [matches, q]);

  const groups = useMemo(() => {
    return {
      live: filtered.filter((m) => m.status === "live" || m.status === "paused"),
      scheduled: filtered.filter((m) => m.status === "scheduled"),
      finished: filtered.filter((m) => m.status === "finished"),
    };
  }, [filtered]);

  const startMatch = async (id) => {
    try {
      await api.post(`/matches/${id}/start`);
      toast.success("Match started");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="referee-portal">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Referee Portal</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
            Match<br/><span className="text-[#F4B400]">Control</span>
          </h1>
          <div className="mt-3 text-zinc-400 text-sm max-w-xl">
            Welcome, {user.name}. Pick a match below to open the umpire panel with live scoring, clocks, and event log.
          </div>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            data-testid="ref-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search team, court…"
            className="pl-9 bg-[#11241b] border-white/10 text-white"
          />
        </div>
      </div>

      {/* Quick metrics */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <Metric label="Live now" value={groups.live.length} accent="#E32424" testid="ref-metric-live" />
        <Metric label="Up next" value={groups.scheduled.length} accent="#F4B400" testid="ref-metric-scheduled" />
        <Metric label="Finished" value={groups.finished.length} accent="#00A86B" testid="ref-metric-finished" />
      </div>

      {loading && <div className="text-zinc-500 text-sm">Loading matches…</div>}

      {/* Live */}
      {groups.live.length > 0 && (
        <Section title="On Court" accent="#E32424" testid="ref-section-live">
          <div className="grid md:grid-cols-2 gap-4">
            {groups.live.map((m) => <MatchCard key={m.id} m={m} onOpen={() => nav(m.format_type === "set_based" ? `/scoring/${m.id}` : `/umpire/${m.id}`)} />)}
          </div>
        </Section>
      )}

      {/* Scheduled */}
      {groups.scheduled.length > 0 && (
        <Section title="Up Next" accent="#F4B400" testid="ref-section-scheduled">
          <div className="grid md:grid-cols-2 gap-4">
            {groups.scheduled.map((m) => (
              <MatchCard
                key={m.id}
                m={m}
                onOpen={() => nav(m.format_type === "set_based" ? `/scoring/${m.id}` : `/umpire/${m.id}`)}
                onStart={() => startMatch(m.id)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Finished */}
      {groups.finished.length > 0 && (
        <Section title="Finished" accent="#00A86B" testid="ref-section-finished">
          <div className="grid md:grid-cols-2 gap-4">
            {groups.finished.map((m) => <MatchCard key={m.id} m={m} onOpen={() => nav(m.format_type === "set_based" ? `/scoring/${m.id}` : `/umpire/${m.id}`)} readOnly />)}
          </div>
        </Section>
      )}

      {!loading && filtered.length === 0 && (
        <div
          className="text-zinc-500 text-sm text-center py-12"
          data-testid="ref-empty"
        >
          {user?.role === "court_umpire"
            ? "No matches assigned to you yet. Ask an admin to assign you as referee for a match."
            : "No matches found."}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-5 rounded-xl border border-white/10 bg-[#11241b]"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-anton text-5xl tracking-tighter mt-1 text-white">{value}</div>
    </div>
  );
}

function Section({ title, accent, children, testid }) {
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-2 h-2 rounded-full" style={{ background: accent }} />
        <h2 className="font-anton uppercase text-2xl tracking-tight text-white">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function MatchCard({ m, onOpen, onStart, readOnly }) {
  const style = STATUS_STYLE[m.status] || STATUS_STYLE.scheduled;
  return (
    <Card className="p-5 bg-[#11241b] border-white/10" data-testid={`ref-match-${m.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          {m.court} · {m.stage}
        </span>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${style}`}>
          {m.status === "live" && <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse mr-1.5" />}
          {m.status}
        </span>
      </div>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="font-anton text-2xl uppercase text-white truncate">{m.team_a?.name || "—"}</div>
        <div className="font-mono-score text-3xl text-[#F4B400]">
          {m.format_type === "set_based"
            ? (m.set_scores || []).map(s => `${s.a}-${s.b}`).join(", ") || "0-0"
            : `${m.score_a} : ${m.score_b}`}
        </div>
        <div className="font-anton text-2xl uppercase text-white text-right truncate">{m.team_b?.name || "—"}</div>
      </div>
      <div className="text-xs text-zinc-500 mb-3 flex items-center gap-3">
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(m.match_remaining)}</span>
        <span>Period {m.current_period}</span>
        {m.scheduled_at && <span className="truncate">{m.scheduled_at}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {!readOnly && m.status === "scheduled" && onStart && (
          <Button
            data-testid={`ref-start-${m.id}`}
            size="sm"
            className="bg-[#00A86B] hover:bg-[#00935A] text-white"
            onClick={onStart}
          >
            <Play className="w-3.5 h-3.5 mr-1.5" /> Start Match
          </Button>
        )}
        <Button
          data-testid={`ref-open-${m.id}`}
          size="sm"
          className="bg-[#F4B400] hover:bg-[#e5aa00] text-black font-bold"
          onClick={onOpen}
        >
          <Radio className="w-3.5 h-3.5 mr-1.5" /> {readOnly ? "Review" : "Open Panel"}
        </Button>
      </div>
    </Card>
  );
}
